import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export function useImageUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadImage = async (file: File): Promise<UploadResult> => {
    setUploading(true);
    setProgress(10);

    try {
      // Validate file
      if (!file.type.startsWith('image/')) {
        throw new Error('Only image files are allowed');
      }

      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      setProgress(30);

      // Get current user for path - required by RLS policy
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be logged in to upload images');
      }

      // Generate unique filename with user ID prefix (required by storage RLS policy)
      const fileExt = file.name.split('.').pop() || 'png';
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      setProgress(50);

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('nft-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        throw new Error(error.message || 'Upload failed');
      }

      setProgress(80);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('nft-images')
        .getPublicUrl(data.path);

      setProgress(100);

      console.log('Image upload successful:', urlData.publicUrl);

      return {
        success: true,
        url: urlData.publicUrl,
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      console.error('Image upload error:', message);
      toast.error(`Upload failed: ${message}`);
      return {
        success: false,
        error: message,
      };
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  // Keep backward compatibility - alias for old IPFS function name
  const uploadToIpfs = uploadImage;

  return {
    uploadImage,
    uploadToIpfs,
    uploading,
    progress,
  };
}

// Export alias for backward compatibility
export { useImageUpload as useIpfsUpload };
